import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, FormActions, Input } from "../../shared";
import { Select, SelectOption } from "../../shared/Select";
import { Icons } from "../../shared/Icons";
import { RecurrencyAttributes, Client, Professional } from "../../../types";
import { clientsApi } from "../../../services/clientsApi";
import { servicesApi, professionalsApi } from "../../../services";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import { recurrenciesApi } from "../../../services/recurrenciesApi";
import { useCompany } from "../../../contexts/CompanyContext";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { MercosulPlateBadge } from "../../shared/MercosulPlateBadge";
import { formatServiceSelectLabel } from "../../../utils/serviceEffortPrice";

interface RecurrencyFormData {
  companyId: number;
  clientId: number;
  serviceId: number;
  price: string;
  clientCarId?: number;
  professionalId?: number;
  startTime?: string;
  weeks: string;
  initialDate: string;
}

interface CreateRecurrencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  editingRecurrency?: RecurrencyAttributes | null;
}

export const CreateRecurrencyModal: React.FC<CreateRecurrencyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  editingRecurrency,
}) => {
  const { company } = useCompany();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [cars, setCars] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const [formData, setFormData] = useState<RecurrencyFormData>({
    companyId,
    clientId: 0,  
    serviceId: 0,
    price: "",
    clientCarId: undefined,
    professionalId: undefined,
    startTime: undefined,
    weeks: "2",
    initialDate: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });

  const fetchCars = useCallback(async (clientId: number) => {
    try {
      const carsResponse = await clientsApi.getCarsByClient(clientId);
      if (carsResponse.success && carsResponse.data) {
        setCars(carsResponse.data);
      } else {
        setCars([]);
      }
    } catch (err) {
      console.error("Error fetching cars:", err);
      setCars([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoadingData(true);

      // Fetch clients
      const clientsResponse = await clientsApi.getClientsByCompany();
      if (clientsResponse.success && clientsResponse.data) {
        setClients(clientsResponse.data);
      }

      // Fetch services
      const servicesResponse = await servicesApi.getServicesByCompany();
      if (servicesResponse.success && servicesResponse.data) {
        setServices(servicesResponse.data);
      }

      // Fetch professionals if not car company
      if (company?.category !== "car") {
        const professionalsResponse = await professionalsApi.getProfessionalsByCompany();
        if (professionalsResponse.success && professionalsResponse.data) {
          const activeProfessionals = professionalsResponse.data.filter((p) => p.isActive !== false);
          setProfessionals(activeProfessionals);
          
          // If there's only one professional, set it as default (if not editing or if editing but no professionalId set)
          if (activeProfessionals.length === 1) {
            if (!editingRecurrency || !editingRecurrency.professionalId) {
              setFormData((prev) => ({
                ...prev,
                professionalId: activeProfessionals[0].id,
              }));
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erro ao carregar dados");
    } finally {
      setLoadingData(false);
    }
  }, [companyId, company?.category, editingRecurrency]);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchData();
    }
  }, [isOpen, companyId, fetchData]);

  useEffect(() => {
    if (editingRecurrency) {
      // Format initialDate if it exists, otherwise use today
      let initialDate = new Date().toISOString().split('T')[0];
      if (editingRecurrency.createdAt) {
        const date = new Date(editingRecurrency.createdAt);
        initialDate = date.toISOString().split('T')[0];
      }
      
      setFormData({
        companyId: editingRecurrency.companyId,
        clientId: editingRecurrency.clientId,
        serviceId: editingRecurrency.serviceId,
        price: editingRecurrency.price,
        clientCarId: editingRecurrency.clientCarId,
        professionalId: editingRecurrency.professionalId || undefined,
        startTime: (editingRecurrency as any).startTime || undefined,
        weeks: String(editingRecurrency.weeks || 2),
        initialDate,
      });
      if (editingRecurrency.clientId && company?.category === "car") {
        fetchCars(editingRecurrency.clientId);
      }
    } else {
      setFormData({
        companyId,
        clientId: 0,
        serviceId: 0,
        price: "",
        clientCarId: undefined,
        professionalId: undefined,
        startTime: undefined,
        weeks: "2",
        initialDate: new Date().toISOString().split('T')[0],
      });
      setCars([]);
    }
    setError(null);
  }, [editingRecurrency, companyId, isOpen, company?.category, fetchCars]);

  useEffect(() => {
    if (formData.clientId && company?.category === "car") {
      fetchCars(formData.clientId);
    } else {
      setCars([]);
      setFormData((prev) => ({ ...prev, clientCarId: undefined }));
    }
  }, [formData.clientId, company?.category, fetchCars]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.clientId || formData.clientId === 0) {
      setError("Cliente é obrigatório");
      return;
    }
    if (!formData.serviceId || formData.serviceId === 0) {
      setError("Serviço é obrigatório");
      return;
    }
    const price = Number(formData.price);
    if (!formData.price.trim() || isNaN(price) || price <= 0) {
      setError("Preço deve ser maior que zero");
      return;
    }
    const weeks = Number(formData.weeks);
    if (!formData.weeks.trim() || isNaN(weeks) || weeks <= 0) {
      setError("Semanas deve ser maior que zero");
      return;
    }
    if (!formData.initialDate) {
      setError("Data inicial é obrigatória");
      return;
    }
    // Validate professionalId for non-car companies
    if (company?.category !== "car" && (!formData.professionalId || formData.professionalId === 0)) {
      setError("Profissional é obrigatório");
      return;
    }
    // Validate startTime for non-car companies
    if (company?.category !== "car" && !formData.startTime) {
      setError("Horário é obrigatório");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ensure numeric fields are numbers
      const submitData: any = {
        ...formData,
        clientId: Number(formData.clientId),
        serviceId: Number(formData.serviceId),
        clientCarId: Number(formData.clientCarId),
        weeks,
        price,
      };

      // Only include professionalId for non-car companies
      if (company?.category !== "car" && formData.professionalId) {
        submitData.professionalId = Number(formData.professionalId);
      }

      // Only include startTime for non-car companies
      if (company?.category !== "car" && formData.startTime) {
        submitData.startTime = formData.startTime;
      }

      let response;
      if (editingRecurrency) {
        response = await recurrenciesApi.updateRecurrency(editingRecurrency.id, submitData);
      } else {
        const createData: any = {
          ...submitData,
          client: clients.find((c) => c.id === submitData.clientId)!,
          service: services.find((s) => s.id === submitData.serviceId)!,
        };
        
        // Only include clientCar if company category is "car"
        if (company?.category === "car" && submitData.clientCarId > 0) {
          createData.clientCar = cars.find((c) => c.id === submitData.clientCarId);
        }
        
        response = await recurrenciesApi.createRecurrency(createData);
      }

      if (response.success) {
        setLoading(false);
        onSuccess();
        onClose();
      } else {
        setError(response.error || "Falha ao salvar recorrência");
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || "Falha ao salvar recorrência");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      companyId,
      clientId: 0,
      serviceId: 0,
      price: "",
      clientCarId: undefined,
      professionalId: undefined,
      startTime: undefined,
      weeks: "2",
      initialDate: new Date().toISOString().split('T')[0],
    });
    setError(null);
    setCars([]);
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    // Fields that should be numbers (weeks/price kept as strings so the input can be cleared while typing)
    const numberFields = ["clientId", "serviceId", "clientCarId", "professionalId"];
    const stringNumberFields = ["weeks", "price"];

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : stringNumberFields.includes(name)
          ? value
          : type === "number" || numberFields.includes(name)
          ? parseFloat(value) || 0
          : value,
    }));

    if (error) setError(null);
  };

  const handleSelectChange = (
    name: keyof RecurrencyFormData,
    value: string | number
  ) => {
    const numberFields = [
      "clientId",
      "serviceId",
      "clientCarId",
      "professionalId",
    ];

    setFormData((prev) => ({
      ...prev,
      [name]: numberFields.includes(name)
        ? parseFloat(value.toString()) || 0
        : value,
    }));

    if (error) setError(null);
  };

  const clientOptions: SelectOption[] = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id.toString(),
        label: `${client.name} - ${formatPhoneNumber(client.phoneNumber)}`,
      })),
    [clients]
  );

  const serviceOptions: SelectOption[] = useMemo(
    () =>
      services.map((service) => ({
        value: service.id.toString(),
        label: formatServiceSelectLabel(service),
      })),
    [services]
  );

  const professionalOptions: SelectOption[] = useMemo(
    () =>
      professionals.map((professional) => ({
        value: professional.id.toString(),
        label: professional.name,
      })),
    [professionals]
  );

  const carOptions: SelectOption[] = useMemo(
    () => [
      { value: "0", label: "Nenhum veículo específico" },
      ...cars.map((car) => ({
        value: car.id.toString(),
        label: `${car.model} - ${car.plate}`,
      })),
    ],
    [cars]
  );

  const selectedCar = cars.find((car) => car.id === formData.clientCarId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingRecurrency ? "Editar Recorrência" : "Criar Nova Recorrência"}
      icon={<Icons.Repeat sx={{ fontSize: 24 }} />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <Icons.Warning sx={{ fontSize: 24, color: "#b91c1c" }} />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {loadingData ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Carregando dados...</span>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {/* Client Selection */}
          <Select
            id="clientId"
            name="clientId"
            label="Cliente"
            value={formData.clientId ? String(formData.clientId) : ""}
            onChange={(value) => handleSelectChange("clientId", value)}
            options={clientOptions}
            placeholder="Selecione um cliente"
            required
            searchable
            searchPlaceholder="Buscar cliente..."
            disabled={loading}
          />

          {/* Service Selection */}
          <Select
            id="serviceId"
            name="serviceId"
            label="Serviço"
            value={formData.serviceId ? String(formData.serviceId) : ""}
            onChange={(value) => handleSelectChange("serviceId", value)}
            options={serviceOptions}
            placeholder="Selecione um serviço"
            required
            searchable
            searchPlaceholder="Buscar serviço..."
            disabled={loading}
          />

          {/* Professional Selection - Only for non-car companies, and only if there's more than one professional */}
          {company?.category !== "car" && professionals.length > 1 && (
            <Select
              id="professionalId"
              name="professionalId"
              label="Profissional"
              value={formData.professionalId ? String(formData.professionalId) : ""}
              onChange={(value) => handleSelectChange("professionalId", value)}
              options={professionalOptions}
              placeholder="Selecione um profissional"
              required
              searchable
              searchPlaceholder="Buscar profissional..."
              disabled={loading}
            />
          )}

          {/* Start Time - Only for non-car companies */}
          {company?.category !== "car" && (
            <Input
              id="startTime"
              name="startTime"
              type="time"
              label="Horário *"
              value={formData.startTime || ""}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit();
                } else if (e.key === "Escape") {
                  handleClose();
                }
              }}
              required
              disabled={loading}
            />
          )}

          {/* Client Car Selection - Only for car companies */}
          {company?.category === "car" && formData.clientId > 0 && (
            <>
              <Select
                id="clientCarId"
                name="clientCarId"
                label="Veículo do Cliente"
                value={formData.clientCarId ? String(formData.clientCarId) : "0"}
                onChange={(value) => handleSelectChange("clientCarId", value)}
                options={carOptions}
                placeholder="Nenhum veículo específico"
                searchable={cars.length > 5}
                searchPlaceholder="Buscar veículo..."
                disabled={loading || cars.length === 0}
              />
              {selectedCar ? (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-slate-50 px-3 py-3">
                  <CarBrandLogo brand={selectedCar.brand} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 break-words">
                      {selectedCar.model}
                    </p>
                    {selectedCar.effort ? (
                      <p className="text-xs text-gray-500 mt-0.5">{selectedCar.effort}</p>
                    ) : null}
                  </div>
                  <MercosulPlateBadge plate={selectedCar.plate} size="sm" />
                </div>
              ) : null}
            </>
          )}

          {/* Initial Date */}
          <Input
            id="initialDate"
            name="initialDate"
            type="date"
            label="Data Inicial"
            value={formData.initialDate}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              } else if (e.key === "Escape") {
                handleClose();
              }
            }}
            required
            disabled={loading}
          />

          {/* Price and Weeks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              id="price"
              name="price"
              type="number"
              label="Preço (R$)"
              value={formData.price}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit();
                } else if (e.key === "Escape") {
                  handleClose();
                }
              }}
              min={0}
              step={0.01}
              placeholder="0.00"
              required
              disabled={loading}
            />

            <Input
              id="weeks"
              name="weeks"
              type="number"
              label="Semanas"
              value={formData.weeks}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit();
                } else if (e.key === "Escape") {
                  handleClose();
                }
              }}
              min={1}
              placeholder="2"
              required
              disabled={loading}
            />
          </div>

          {/* Form Actions */}
          <FormActions
            onCancel={handleClose}
            onSubmit={handleSubmit}
            loading={loading}
            isEditing={!!editingRecurrency}
            submitType="button"
            submitText={
              editingRecurrency ? "Atualizar Recorrência" : "Criar Recorrência"
            }
          />
        </div>
      )}
    </Modal>
  );
};
